/**
 * AI Planning V1 – frontend API client
 * Calls backend /api/v1/ai/planning (session, message, freeze, resume, save, download).
 */

import api from './api';

const PLANNING_BASE = '/ai/planning';

/** Planning device ID for anonymous sessions. Persists in localStorage; used to claim sessions on signup. */
export function getPlanningDeviceId(): string {
  const key = 'tmos_planning_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = `dev_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

function getDeviceId(): string {
  return getPlanningDeviceId();
}

function planningHeaders(): Record<string, string> {
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Device-Id': getDeviceId(),
  };
  const token = localStorage.getItem('auth_token');
  if (token) {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
}

export interface PlanningSession {
  id: string;
  identityId: string | null;
  userId: string | null;
  tenantId: string | null;
  currentStep: string;
  currentStage?: string;
  mode?: 'discuss' | 'bake';
  draftVersion?: number;
  layerState?: Record<string, { locked?: boolean; stale?: boolean }> | null;
  myRole?: 'viewer' | 'discuss' | 'plan' | 'admin' | null;
  isFrozen: boolean;
  frozenAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Meaningful name from first user message (e.g. first sentence). */
  name?: string | null;
}

export interface CreateSessionResponse {
  success: boolean;
  session: PlanningSession;
}

export interface PostMessageResponse {
  success: boolean;
  reply?: string;
  tokensUsed?: number;
  /** Updated draft after this reply (so the right-hand preview can update immediately). */
  draft?: DraftSnapshot | null;
  /** Updated conversation name (set after first user message or from vision). */
  sessionName?: string | null;
  /** Updated timestamp after message (for display). */
  sessionUpdatedAt?: string | null;
  /** When true, show an Apply/Build button so the user can confirm without typing. */
  showApplyButton?: boolean;
  mode?: 'discuss' | 'bake' | null;
  currentStage?: string | null;
  layerState?: Record<string, { locked?: boolean; stale?: boolean }> | null;
  draftVersion?: number | null;
  collaborative?: boolean;
  requireLogin?: boolean;
  message?: string;
}

export interface FreezeResponse {
  success: boolean;
  frozen: boolean;
}

export interface PlanningChatMessageDto {
  id: string;
  role: string;
  content: string;
  createdAt: string;
  userId?: string | null;
  userDisplayName?: string | null;
  userAvatarUrl?: string | null;
}

export interface ResumeResponse {
  success: boolean;
  resumed: boolean;
  messages: PlanningChatMessageDto[];
}

export interface SaveResponse {
  success: boolean;
  roadmapId?: string;
  savedAt?: string;
  planId?: string | null;
}

export interface DraftReference {
  id: string;
  source: string;
  description?: string;
}

/** Draft snapshot returned by GET /session/:sessionId/draft */
export interface DraftSnapshot {
  vision?: Record<string, unknown> | null;
  objectives?: Record<string, unknown> | null;
  strategies?: Record<string, unknown> | null;
  projects?: Record<string, unknown> | null;
  references?: DraftReference[];
}

export interface GetDraftResponse {
  success: boolean;
  draft: DraftSnapshot | null;
}

/** Session summary for list (recent conversations within retention window). type indicates which screen opens it (e.g. strap). */
export interface PlanningSessionSummary {
  id: string;
  /** Meaningful name from first user message. */
  name?: string | null;
  createdAt: string;
  updatedAt: string;
  isFrozen: boolean;
  messageCount: number;
  type?: 'strap';
}

export interface ListSessionsResponse {
  success: boolean;
  sessions: PlanningSessionSummary[];
}

export interface LoadSessionResponse {
  success: boolean;
  session: PlanningSession | null;
  messages: PlanningChatMessageDto[];
  /** Draft for the session (so preview shows immediately when loading). */
  draft?: DraftSnapshot | null;
}

export async function listSessions(): Promise<ListSessionsResponse> {
  const { data } = await api.get<ListSessionsResponse>(`${PLANNING_BASE}/sessions`, { headers: planningHeaders() });
  return data;
}

export async function loadSession(sessionId: string): Promise<LoadSessionResponse> {
  const { data } = await api.get<LoadSessionResponse>(`${PLANNING_BASE}/session/${sessionId}`, { headers: planningHeaders() });
  return data;
}

export async function createSession(identityId?: string | null, tenantId?: string | null): Promise<CreateSessionResponse> {
  const body: { identityId?: string; tenantId?: string } = {};
  if (identityId != null && identityId !== '') body.identityId = identityId;
  if (tenantId != null && tenantId !== '') body.tenantId = tenantId;
  const { data } = await api.post<CreateSessionResponse>(`${PLANNING_BASE}/session`, body, { headers: planningHeaders() });
  return data;
}

export async function postMessage(
  sessionId: string,
  message: string,
  options?: { attachedDocumentIds?: string[]; modelConfigurationId?: string | null }
): Promise<PostMessageResponse> {
  const body: { message: string; attachedDocumentIds?: string[]; modelConfigurationId?: string | null } = { message };
  if (options?.attachedDocumentIds && options.attachedDocumentIds.length > 0) {
    body.attachedDocumentIds = options.attachedDocumentIds;
  }
  if (options?.modelConfigurationId != null && options.modelConfigurationId !== '') {
    body.modelConfigurationId = options.modelConfigurationId;
  }
  const { data } = await api.post<PostMessageResponse>(
    `${PLANNING_BASE}/session/${sessionId}/message`,
    body,
    { headers: planningHeaders(), timeout: 60000 }
  );
  return data;
}

/** Payload of the "done" SSE event from POST .../message/stream (same shape as PostMessageResponse). */
export interface PostMessageStreamDonePayload {
  success: boolean;
  reply?: string;
  tokensUsed?: number;
  draft?: DraftSnapshot | null;
  sessionName?: string | null;
  sessionUpdatedAt?: string | null;
  showApplyButton?: boolean;
  mode?: 'discuss' | 'bake' | null;
  currentStage?: string | null;
  layerState?: Record<string, { locked?: boolean; stale?: boolean }> | null;
  draftVersion?: number | null;
  collaborative?: boolean;
  requireLogin?: boolean;
  message?: string;
  /** Backend planning quota denial code (403 or SSE). */
  code?: string;
  /** When requireLogin: cap (daily limit) and used tokens for debugging. */
  limitDebug?: { cap: number; used: number } | { periodQuota: number; periodUsed: number; topUpBalanceUsd: number };
}

/** True when the client should show the planning quota assistant message (not a generic network error). */
export function isPlanningQuotaPayload(payload: PostMessageStreamDonePayload): boolean {
  if (payload.requireLogin === true) return true;
  const c = payload.code;
  return (
    c === 'QUOTA_EXCEEDED' ||
    c === 'FREE_DAILY_QUOTA_EXCEEDED' ||
    c === 'ANONYMOUS_QUOTA_EXCEEDED'
  );
}

export type PlanningUsageSummary =
  | { success: true; kind: 'daily'; usedTokens: number; capTokens: number; windowHours: number }
  | { success: true; kind: 'anonymous'; usedTokens: number; capTokens: number; windowHours: number }
  | {
      success: true;
      kind: 'subscription';
      usedTokens: number;
      capTokens: number;
      remainingTokens: number;
      topUpBalanceUsd: number;
      periodEndsAt: string;
    }
  | { success: false; message?: string };

function parseStreamPayload(data: Record<string, unknown>): PostMessageStreamDonePayload {
  return {
    success: (data.success as boolean) ?? false,
    reply: data.reply as string | undefined,
    tokensUsed: data.tokensUsed as number | undefined,
    draft: data.draft as DraftSnapshot | null | undefined,
    sessionName: data.sessionName as string | null | undefined,
    sessionUpdatedAt: data.sessionUpdatedAt as string | null | undefined,
    showApplyButton: data.showApplyButton as boolean | undefined,
    mode: data.mode as PostMessageStreamDonePayload['mode'],
    currentStage: data.currentStage as string | null | undefined,
    layerState: data.layerState as PostMessageStreamDonePayload['layerState'],
    draftVersion: data.draftVersion as number | null | undefined,
    collaborative: data.collaborative as boolean | undefined,
    requireLogin: data.requireLogin as boolean | undefined,
    message: data.message as string | undefined,
    code: typeof data.code === 'string' ? data.code : undefined,
    limitDebug: data.limitDebug as PostMessageStreamDonePayload['limitDebug'],
  };
}

/**
 * Send a message and consume the reply via Server-Sent Events (light call streamed).
 * Calls onChunk(text) for each streamed chunk, then onDone(payload) with the final reply/draft/etc.
 * On error, onDone is called exactly once with success: false (quota codes preserved for UX).
 */
export async function postMessageStream(
  sessionId: string,
  message: string,
  options: {
    attachedDocumentIds?: string[];
    modelConfigurationId?: string | null;
    onChunk: (text: string) => void;
    onDone: (payload: PostMessageStreamDonePayload) => void;
  }
): Promise<void> {
  const body: { message: string; attachedDocumentIds?: string[]; modelConfigurationId?: string | null } = { message };
  if (options.attachedDocumentIds && options.attachedDocumentIds.length > 0) {
    body.attachedDocumentIds = options.attachedDocumentIds;
  }
  if (options.modelConfigurationId != null && options.modelConfigurationId !== '') {
    body.modelConfigurationId = options.modelConfigurationId;
  }

  let finished = false;
  const done = (payload: PostMessageStreamDonePayload) => {
    if (finished) return;
    finished = true;
    options.onDone(payload);
  };

  const friendlyNetworkMessage = (raw: string): string => {
    const s = raw.trim();
    if (/network error/i.test(s) || /^failed to fetch$/i.test(s) || /load failed/i.test(s)) {
      return 'We could not reach the server. Check your connection and try again. If you were out of AI tokens, open Subscription in your profile to review your plan.';
    }
    return s;
  };

  try {
    const baseUrl = getApiBaseUrl();
    const res = await fetch(`${baseUrl}${PLANNING_BASE}/session/${sessionId}/message/stream`, {
      method: 'POST',
      headers: planningHeaders(),
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(120000),
    });

    if (!res.ok) {
      const raw = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      const message =
        (typeof raw.message === 'string' && raw.message) ||
        (typeof raw.error === 'string' && raw.error) ||
        res.statusText ||
        'Request failed';
      done({
        success: false,
        requireLogin: raw.requireLogin === true,
        message,
        code: typeof raw.code === 'string' ? raw.code : undefined,
        limitDebug: raw.limitDebug as PostMessageStreamDonePayload['limitDebug'],
      });
      return;
    }

    const reader = res.body?.getReader();
    const decoder = new TextDecoder();
    if (!reader) {
      done({ success: false, message: 'No response body', code: 'STREAM_INCOMPLETE' });
      return;
    }

    let buffer = '';
    let currentEvent = '';
    let currentData = '';
    let reading = true;
    while (reading) {
      const { done: streamDone, value } = await reader.read();
      if (streamDone) {
        reading = false;
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        if (!part.trim()) continue;
        const lines = part.split('\n');
        currentEvent = '';
        currentData = '';
        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
          }
        }
        if (!currentData) continue;
        try {
          const data = JSON.parse(currentData) as Record<string, unknown>;
          if (currentEvent === 'chunk' && typeof data.text === 'string') {
            options.onChunk(data.text);
          } else if (currentEvent === 'done' || currentEvent === 'error') {
            done(parseStreamPayload(data));
            return;
          }
        } catch {
          // skip malformed chunk
        }
      }
    }

    if (buffer.trim()) {
      const lines = buffer.split('\n');
      for (const line of lines) {
        if (line.startsWith('event:')) currentEvent = line.slice(6).trim();
        else if (line.startsWith('data:')) currentData = line.slice(5).trim();
      }
      if (currentData) {
        try {
          const data = JSON.parse(currentData) as Record<string, unknown>;
          if (currentEvent === 'done' || currentEvent === 'error') {
            done(parseStreamPayload(data));
            return;
          }
        } catch {
          done({ success: false, message: 'Invalid stream end', code: 'STREAM_INCOMPLETE' });
          return;
        }
      }
    }

    done({
      success: false,
      message:
        'The connection closed before the assistant finished. Please try again. If this keeps happening, check Subscription for token limits.',
      code: 'STREAM_INCOMPLETE',
    });
  } catch (e) {
    const raw = e instanceof Error ? e.message : 'Stream failed';
    done({
      success: false,
      message: friendlyNetworkMessage(raw),
      code: 'FETCH_FAILED',
    });
  }
}

export async function freezeSession(sessionId: string): Promise<FreezeResponse> {
  const { data } = await api.post<FreezeResponse>(`${PLANNING_BASE}/session/${sessionId}/freeze`, {}, { headers: planningHeaders() });
  return data;
}

export async function resumeSession(sessionId: string): Promise<ResumeResponse> {
  const { data } = await api.post<ResumeResponse>(`${PLANNING_BASE}/session/${sessionId}/resume`, {}, { headers: planningHeaders() });
  return data;
}

export async function saveToWorkspace(sessionId: string, title?: string): Promise<SaveResponse> {
  const { data } = await api.post<SaveResponse>(`${PLANNING_BASE}/session/${sessionId}/save`, { title }, { headers: planningHeaders() });
  return data;
}

/** Flag session so the roadmap is saved to the user's account when they sign in (anonymous only). */
export async function flagPendingSave(sessionId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await api.post<{ success: boolean; message?: string }>(
    `${PLANNING_BASE}/session/${sessionId}/flag-pending-save`,
    {},
    { headers: planningHeaders() }
  );
  return data;
}

export async function getDraft(sessionId: string): Promise<GetDraftResponse> {
  const { data } = await api.get<GetDraftResponse>(`${PLANNING_BASE}/session/${sessionId}/draft`, { headers: planningHeaders() });
  return data;
}

export interface DeleteSessionResponse {
  success: boolean;
  message?: string;
}

export async function deleteSession(sessionId: string): Promise<DeleteSessionResponse> {
  const { data } = await api.delete<DeleteSessionResponse>(`${PLANNING_BASE}/session/${sessionId}`, { headers: planningHeaders() });
  return data;
}

export interface UpdateSessionNameResponse {
  success: boolean;
  name?: string | null;
  message?: string;
}

export async function updateSessionName(sessionId: string, name: string): Promise<UpdateSessionNameResponse> {
  const { data } = await api.patch<UpdateSessionNameResponse>(`${PLANNING_BASE}/session/${sessionId}/name`, { name: name.trim().slice(0, 255) }, { headers: planningHeaders() });
  return data;
}

export interface AttachDocumentResponse {
  success: boolean;
  doc?: { id: string; fileName: string; createdAt: string };
  message?: string;
}

function getApiBaseUrl(): string {
  const env = import.meta.env;
  const url = (env as { VITE_API_URL?: string }).VITE_API_URL;
  if (url) return url.replace(/\/$/, '') + '/api/v1';
  return env.DEV ? '/api/v1' : 'http://localhost:3000/api/v1';
}

export async function fetchPlanningUsageSummary(): Promise<PlanningUsageSummary> {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}${PLANNING_BASE}/usage-summary`, { headers: planningHeaders() });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const msg =
      (typeof data.message === 'string' && data.message) ||
      (typeof data.error === 'string' && data.error) ||
      res.statusText;
    return { success: false, message: msg };
  }
  if (data.success !== true || typeof data.kind !== 'string') {
    return { success: false, message: 'Invalid usage summary' };
  }
  return data as unknown as PlanningUsageSummary;
}

export async function attachDocument(sessionId: string, file: File): Promise<AttachDocumentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('auth_token');
  const deviceId = getDeviceId();
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}${PLANNING_BASE}/session/${sessionId}/attach`, {
    method: 'POST',
    headers: {
      'X-Device-Id': deviceId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { success: false, message: data.message || `Attach failed: ${res.status}` };
  }
  return data;
}

export interface UploadRoadmapResponse {
  success: boolean;
  draft?: DraftSnapshot | null;
  message?: string;
}

export async function uploadRoadmap(sessionId: string, roadmap: DraftSnapshot): Promise<UploadRoadmapResponse> {
  const { data } = await api.post<UploadRoadmapResponse>(`${PLANNING_BASE}/session/${sessionId}/upload-roadmap`, { roadmap }, { headers: planningHeaders() });
  return data;
}

export function getDownloadUrl(sessionId: string, format: 'text' | 'pdf' | 'docx'): string {
  const params = new URLSearchParams({ format });
  return `${PLANNING_BASE}/session/${sessionId}/download?${params}`;
}

/** Download session export via API (uses same base URL as other requests for proxy/CORS). */
export async function downloadSession(sessionId: string, format: 'text' | 'pdf' | 'docx'): Promise<void> {
  const { data } = await api.get<Blob>(`${PLANNING_BASE}/session/${sessionId}/download`, {
    params: { format },
    headers: planningHeaders(),
    responseType: 'blob',
    timeout: 60000,
  });
  const ext = format === 'text' ? 'txt' : format;
  const blob = data instanceof Blob ? data : new Blob([data]);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `planning-${sessionId.slice(0, 8)}.${ext}`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Download PDF or DOCX with embedded Gantt chart image (captured client-side). */
export async function downloadSessionWithChart(sessionId: string, format: 'pdf' | 'docx', chartImageBase64: string): Promise<void> {
  const { data } = await api.post<Blob>(`${PLANNING_BASE}/session/${sessionId}/download-with-chart`, { format, chartImageBase64 }, {
    headers: planningHeaders(),
    responseType: 'blob',
    timeout: 60000,
  });
  const ext = format;
  const blob = data instanceof Blob ? data : new Blob([data]);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `planning-${sessionId.slice(0, 8)}.${ext}`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Collaborative planning: set Discuss | Bake */
export async function setPlanningMode(
  sessionId: string,
  mode: 'discuss' | 'bake'
): Promise<{ success: boolean; mode?: string; message?: string }> {
  const { data } = await api.patch(`${PLANNING_BASE}/session/${sessionId}/mode`, { mode }, { headers: planningHeaders() });
  return data;
}

export async function setPlanningStage(
  sessionId: string,
  stage: 'vision' | 'objectives' | 'strategies' | 'projects'
): Promise<{
  success: boolean;
  currentStage?: string;
  layerState?: Record<string, { locked?: boolean; stale?: boolean }> | null;
  message?: string;
}> {
  const { data } = await api.patch(`${PLANNING_BASE}/session/${sessionId}/stage`, { stage }, { headers: planningHeaders() });
  return data;
}

export async function listPlanningVersions(
  sessionId: string
): Promise<{
  success: boolean;
  versions?: Array<{ versionNumber: number; layer: string; label: string | null; createdAt: string }>;
  message?: string;
}> {
  const { data } = await api.get(`${PLANNING_BASE}/session/${sessionId}/versions`, { headers: planningHeaders() });
  return data;
}

export async function undoPlanningDraft(
  sessionId: string
): Promise<{ success: boolean; draft?: DraftSnapshot | null; draftVersion?: number; message?: string }> {
  const { data } = await api.post(`${PLANNING_BASE}/session/${sessionId}/undo`, {}, { headers: planningHeaders() });
  return data;
}

export async function restorePlanningVersion(
  sessionId: string,
  versionNumber: number
): Promise<{ success: boolean; draft?: DraftSnapshot | null; draftVersion?: number; message?: string }> {
  const { data } = await api.post(
    `${PLANNING_BASE}/session/${sessionId}/versions/${versionNumber}/restore`,
    {},
    { headers: planningHeaders() }
  );
  return data;
}

export async function listPlanningParticipants(
  sessionId: string
): Promise<{
  success: boolean;
  participants?: Array<{
    userId: string;
    role: string;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  }>;
  message?: string;
}> {
  const { data } = await api.get(`${PLANNING_BASE}/session/${sessionId}/participants`, { headers: planningHeaders() });
  return data;
}

export async function invitePlanningParticipant(
  sessionId: string,
  userId: string,
  role: 'viewer' | 'discuss' | 'plan' | 'admin' = 'discuss'
): Promise<{ success: boolean; message?: string }> {
  const { data } = await api.post(
    `${PLANNING_BASE}/session/${sessionId}/participants`,
    { userId, role },
    { headers: planningHeaders() }
  );
  return data;
}

export async function removePlanningParticipant(
  sessionId: string,
  userId: string
): Promise<{ success: boolean; message?: string }> {
  const { data } = await api.delete(`${PLANNING_BASE}/session/${sessionId}/participants/${userId}`, {
    headers: planningHeaders(),
  });
  return data;
}

export async function getPlanningCollabState(sessionId: string): Promise<{
  success: boolean;
  mode?: 'discuss' | 'bake';
  currentStage?: string;
  draftVersion?: number;
  layerState?: Record<string, { locked?: boolean; stale?: boolean }> | null;
  myRole?: string | null;
  draft?: DraftSnapshot | null;
  updatedAt?: string | null;
  messageCount?: number;
  message?: string;
}> {
  const { data } = await api.get(`${PLANNING_BASE}/session/${sessionId}/collab-state`, {
    headers: planningHeaders(),
  });
  return data;
}
